import { useState, useMemo } from 'react'
import useSWR from 'swr'
import dayjs from 'dayjs'
import {
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  Area,
  ComposedChart,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format } from 'date-fns'
import { cn } from '@/lib/utils'
import { fmtK, fmtPct, fmtMoney } from '@/lib/format-utils'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useRecoveryStore } from '@/stores/recovery-store'
import Loader from '@/components/zthd/Loader'

// Custom label for reference lines
const CustomLabel = ({ viewBox, value, fill }) => {
  const { x, y } = viewBox || {}
  return (
    <text x={x + 5} y={y - 5} fill={fill} fontSize={12} fontWeight="bold">
      {value}
    </text>
  )
}

const FundPool = ({ roasData }) => {
  const { pid, country } = useRecoveryStore()

  // Date range for actual cost data
  const [startDate, setStartDate] = useState(
    dayjs().subtract(180, 'day').toDate(),
  )
  const [endDate, setEndDate] = useState(dayjs().toDate())

  // Query params for actual cost data
  const queryParams = useMemo(
    () => ({
      pid,
      country,
      startDate: dayjs(startDate).format('YYYYMMDD'),
      endDate: dayjs(endDate).format('YYYYMMDD'),
    }),
    [pid, country, startDate, endDate],
  )

  // Fetch actual costs
  const { data: costsData, isLoading } = useSWR(['/costs', queryParams])

  // Build model cumulative rate map (day -> rate as decimal)
  const modelRateMap = useMemo(() => {
    if (!roasData?.length) return {}
    const map = {}
    roasData.forEach((item) => {
      map[item.day] = item.roas / 100
    })
    return map
  }, [roasData])

  // Get max day in model
  const maxModelDay = useMemo(() => {
    if (!roasData?.length) return 0
    return Math.max(...roasData.map((d) => d.day))
  }, [roasData])

  // Get recovery rate for a given day
  const getRecoveryRate = (day) => {
    if (modelRateMap[day] !== undefined) return modelRateMap[day]
    if (day > maxModelDay) return modelRateMap[maxModelDay] || 1
    return 0
  }

  // Calculate fund pool data
  const fundPoolData = useMemo(() => {
    if (!costsData) return []

    // Get all dates in range
    const dates = []
    let currentDate = dayjs(startDate)
    const end = dayjs(endDate)

    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
      dates.push(currentDate.format('YYYYMMDD'))
      currentDate = currentDate.add(1, 'day')
    }

    // Collect daily costs
    const dailyCosts = dates.map((date) => ({
      date,
      cost: costsData?.[date]?.total?.costs || 0,
    }))

    // Calculate fund pool for each day
    const result = []
    let cumulativeCost = 0

    dates.forEach((date, dayIndex) => {
      const dayCost = dailyCosts[dayIndex].cost
      cumulativeCost += dayCost

      // Calculate total unrecovered amount for this day
      // = sum of (each past day's cost × (1 - recovery rate for days elapsed))
      let totalUnrecovered = 0
      let totalRecovered = 0

      for (let i = 0; i <= dayIndex; i++) {
        const costOnDayI = dailyCosts[i].cost
        const daysElapsed = dayIndex - i
        const recoveryRate = getRecoveryRate(daysElapsed)
        const recovered = costOnDayI * recoveryRate
        const unrecovered = costOnDayI * (1 - recoveryRate)

        totalRecovered += recovered
        totalUnrecovered += unrecovered
      }

      const recoveryProgress =
        cumulativeCost > 0 ? (totalRecovered / cumulativeCost) * 100 : 0

      result.push({
        day: dayIndex,
        date,
        dateLabel: dayjs(date).format('MM/DD'),
        dailyCost: dayCost,
        cumulativeCost,
        totalRecovered,
        totalUnrecovered,
        unrecoveredNegative: -totalUnrecovered, // 负值显示
        recoveryProgress,
      })
    })

    return result
  }, [costsData, startDate, endDate, getRecoveryRate])

  // Find peak (maximum unrecovered amount)
  const peakData = useMemo(() => {
    if (!fundPoolData.length) return null

    let peak = fundPoolData[0]
    fundPoolData.forEach((item) => {
      if (item.totalUnrecovered > peak.totalUnrecovered) {
        peak = item
      }
    })
    return peak
  }, [fundPoolData])

  // Summary stats
  const summary = useMemo(() => {
    if (!fundPoolData.length) return null

    const latest = fundPoolData[fundPoolData.length - 1]
    return {
      totalCost: latest.cumulativeCost,
      totalRecovered: latest.totalRecovered,
      currentUnrecovered: latest.totalUnrecovered,
      recoveryProgress: latest.recoveryProgress,
      peakUnrecovered: peakData?.totalUnrecovered || 0,
      peakDay: peakData?.day || 0,
      peakDate: peakData?.dateLabel || '',
    }
  }, [fundPoolData, peakData])

  // Y axis range for chart
  const yAxisRange = useMemo(() => {
    if (!fundPoolData.length) return { min: -10000, max: 10000 }
    const maxUnrecovered = Math.max(
      ...fundPoolData.map((d) => d.totalUnrecovered),
    )
    const maxDailyCost = Math.max(...fundPoolData.map((d) => d.dailyCost))
    const min = -Math.ceil(maxUnrecovered / 10000) * 10000
    const max = Math.ceil(maxDailyCost / 1000) * 1000
    return { min, max }
  }, [fundPoolData])

  const handleDateSelect = (range) => {
    if (range?.from) setStartDate(range.from)
    if (range?.to) setEndDate(range.to)
  }

  if (!roasData || roasData.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-slate-500">
        暂无回收模型数据
      </div>
    )
  }

  return (
    <div className="space-y-6 py-4">
      {/* Date Picker */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">数据时间范围</CardTitle>
          <p className="text-sm text-slate-500">选择要分析的历史投放时间范围</p>
        </CardHeader>
        <CardContent>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-[300px] justify-start text-left font-normal',
                  !startDate && 'text-muted-foreground',
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate && endDate ? (
                  <>
                    {format(startDate, 'yyyy-MM-dd')} ~{' '}
                    {format(endDate, 'yyyy-MM-dd')}
                  </>
                ) : (
                  <span>选择日期范围</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={startDate}
                selected={{ from: startDate, to: endDate }}
                onSelect={handleDateSelect}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
        </CardContent>
      </Card>

      {isLoading ? (
        <Loader>加载成本数据中...</Loader>
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">累计投放</div>
                  <div className="text-2xl font-bold text-slate-700">
                    {fmtMoney(summary.totalCost)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">历史总成本</div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">已回收</div>
                  <div className="text-2xl font-bold text-green-600">
                    {fmtMoney(summary.totalRecovered)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    基于模型计算
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-sm text-slate-500">当前占用</div>
                  <div className="text-2xl font-bold text-amber-600">
                    {fmtMoney(summary.currentUnrecovered)}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">未回收资金</div>
                </CardContent>
              </Card>

              <Card className="border-red-200 bg-red-50">
                <CardContent className="pt-6">
                  <div className="text-sm text-red-600">峰值占用 ⚠️</div>
                  <div className="text-2xl font-bold text-red-600">
                    {fmtMoney(summary.peakUnrecovered)}
                  </div>
                  <div className="mt-1 text-xs text-red-500">
                    第 {summary.peakDay} 天 ({summary.peakDate})
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Fund Pool Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">资金占用曲线</CardTitle>
              <p className="text-sm text-slate-500">
                <span className="text-blue-500">●</span> 当日投放 |{' '}
                <span className="text-red-500">●</span>{' '}
                未回收资金（负值表示占用）
              </p>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={450}>
                <ComposedChart data={fundPoolData}>
                  <defs>
                    <linearGradient
                      id="unrecoveredGradient"
                      x1="0"
                      y1="1"
                      x2="0"
                      y2="0"
                    >
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.5} />
                      <stop
                        offset="95%"
                        stopColor="#ef4444"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="dateLabel"
                    stroke="#64748b"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    yAxisId="left"
                    stroke="#ef4444"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    domain={[yAxisRange.min, 0]}
                    tickFormatter={(value) => fmtK(value)}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    stroke="#3b82f6"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    domain={[0, yAxisRange.max]}
                    tickFormatter={(value) => fmtK(value)}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload
                        return (
                          <div className="rounded-lg border bg-background p-3 shadow-lg">
                            <div className="space-y-1">
                              <p className="font-semibold">
                                第 {data.day} 天 ({data.dateLabel})
                              </p>
                              <p className="text-sm text-blue-600 font-medium">
                                当日投放: {fmtMoney(data.dailyCost)}
                              </p>
                              <p className="text-sm text-slate-500">
                                累计投放: {fmtMoney(data.cumulativeCost)}
                              </p>
                              <hr className="my-1" />
                              <p className="text-sm font-bold text-red-500">
                                未回收（占用）: -
                                {fmtMoney(data.totalUnrecovered)}
                              </p>
                              <p className="text-sm text-green-600">
                                已回收: {fmtMoney(data.totalRecovered)}
                              </p>
                              <p className="text-sm text-slate-500">
                                回收进度: {fmtPct(data.recoveryProgress)}
                              </p>
                            </div>
                          </div>
                        )
                      }
                      return null
                    }}
                  />
                  <Legend />

                  {/* Peak reference line */}
                  {peakData && (
                    <ReferenceLine
                      yAxisId="left"
                      x={peakData.dateLabel}
                      stroke="#dc2626"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      label={
                        <CustomLabel
                          value={`峰值 -${fmtK(peakData.totalUnrecovered)}`}
                          fill="#dc2626"
                        />
                      }
                    />
                  )}

                  {/* Daily Cost (positive, right axis) */}
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="dailyCost"
                    name="当日投放"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                  />

                  {/* Unrecovered area (negative, left axis) */}
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="unrecoveredNegative"
                    name="未回收（占用）"
                    stroke="#ef4444"
                    fill="url(#unrecoveredGradient)"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Key Days Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">关键节点</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-[400px] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead>天数</TableHead>
                      <TableHead>日期</TableHead>
                      <TableHead className="text-right">当日投放</TableHead>
                      <TableHead className="text-right">累计投放</TableHead>
                      <TableHead className="text-right">累计已回收</TableHead>
                      <TableHead className="text-right">累计未回收</TableHead>
                      <TableHead className="text-right">回收进度</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fundPoolData
                      .filter(
                        (_, i) =>
                          i % 7 === 0 ||
                          i === fundPoolData.length - 1 ||
                          (peakData && fundPoolData[i]?.date === peakData.date),
                      )
                      .map((item) => (
                        <TableRow
                          key={item.day}
                          className={
                            peakData && item.date === peakData.date
                              ? 'bg-red-50'
                              : ''
                          }
                        >
                          <TableCell className="font-medium">
                            第 {item.day} 天
                            {peakData && item.date === peakData.date && (
                              <span className="ml-2 text-xs text-red-500">
                                ⚠️ 峰值
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {item.dateLabel}
                          </TableCell>
                          <TableCell className="text-right">
                            {fmtMoney(item.dailyCost)}
                          </TableCell>
                          <TableCell className="text-right">
                            {fmtMoney(item.cumulativeCost)}
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {fmtMoney(item.totalRecovered)}
                          </TableCell>
                          <TableCell className="text-right font-medium text-amber-600">
                            {fmtMoney(item.totalUnrecovered)}
                          </TableCell>
                          <TableCell className="text-right text-blue-600">
                            {fmtPct(item.recoveryProgress)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

export default FundPool
